<template>
  <aside
    class="three-d-assembly-parts-card box"
    data-unit="three-d-assembly-orderable-parts-card"
  >
    <h4 class="has-text-weight-semibold assembly-parts-title">
      {{ $t('threeD.partsOrderableSeparatelyFromAssembly') }}<template v-if="assemblyPartNumber">
        ({{ assemblyPartNumber }})
      </template>
    </h4>

    <button
      class="button is-primary is-small view-assembly-parts"
      :class="{ 'is-active': orderingModeActive }"
      type="button"
      :aria-pressed="orderingModeActive"
      data-unit="view-parts-within-assembly"
      @click="$emit('view-parts')"
    >
      {{ $t('threeD.viewPartsWithinAssemblyForOrdering') }}
    </button>

    <ul class="assembly-parts-list">
      <li
        v-for="item in items"
        :key="item.key"
        class="assembly-part-entry"
        :class="{ 'is-active': item.key === selectedPartKey }"
        :data-part-key="item.key"
      >
        <button
          class="button is-white assembly-part-identify"
          type="button"
          :aria-pressed="item.key === selectedPartKey"
          :disabled="!orderingModeActive"
          @click="$emit('select-part', item)"
        >
          <span class="part-name">{{ item.part.name || item.part.displayName }}</span>
          <span
            v-if="item.part.partNumberVisible !== false && item.part.partNumber"
            class="part-number"
          >
            {{ item.part.partNumber }}
          </span>
        </button>
        <p
          v-if="!item.pricingLoaded"
          class="child-pricing-loading"
          role="status"
          aria-live="polite"
          data-unit="child-pricing-loading"
        >
          <span
            class="icon"
            aria-hidden="true"
          >
            <i class="fas fa-spinner fa-pulse" />
          </span>
          <span>{{ $t('loading') }}</span>
        </p>
        <info-add-to-cart
          :key="item.key"
          :add-to-cart-qty-behavior="addToCartQtyBehavior"
          :content="item.part"
          :display-availability="displayAvailability"
          :display-discounted="displayDiscounted"
          :display-eta="displayEta"
          :display-retail="displayRetail"
          :display-whole-sale="displayWholeSale"
          :is-cart-or-erp-cart-enabled="isCartOrErpCartEnabled"
          :pricing="{ ...item.part, isLoaded: item.pricingLoaded }"
          show-pricing-for-parts
        />
      </li>
    </ul>
  </aside>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useStore } from 'vuex'
import InfoAddToCart from './info/InfoAddToCart.vue'
import type { ThreeDViewerOrderableAssemblyPart } from '@/interfaces/library'

defineProps<{
  assemblyPartNumber: string | null
  items: ThreeDViewerOrderableAssemblyPart[]
  orderingModeActive: boolean
  selectedPartKey: string | null
}>()

defineEmits(['select-part', 'view-parts'])

const store = useStore()
const addToCartQtyBehavior = computed(() => store.state.user.preferences.addToCartQtyBehavior)
const displayAvailability = computed(() => store.getters['user/displayAvailability'])
const displayDiscounted = computed(() => store.getters['user/displayDiscounted'])
const displayEta = computed(() => store.getters['user/displayEta'])
const displayRetail = computed(() => store.getters['user/displayRetail'])
const displayWholeSale = computed(() => store.getters['user/displayWholeSale'])
const isCartOrErpCartEnabled = computed(() => store.getters['user/isCartOrErpCartEnabled'])
</script>

<style scoped>
.three-d-assembly-parts-card {
  font-size: 0.875rem;
  padding: 1rem;
}
.assembly-parts-title {
  margin-bottom: 0.75rem;
}
.view-assembly-parts {
  height: auto;
  margin-bottom: 0.75rem;
  min-height: 2.25rem;
  white-space: normal;
  width: 100%;
}
.assembly-parts-list {
  list-style: none;
  margin: 0;
}
.assembly-part-entry {
  border-top: 1px solid var(--grey-light, #dbdbdb);
  padding: 0.75rem 0;
}
.assembly-part-entry.is-active {
  box-shadow: inset 3px 0 0 var(--primary, #3273dc);
  padding-left: 0.5rem;
}
.assembly-part-identify {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  height: auto;
  justify-content: flex-start;
  padding: 0;
  text-align: left;
  white-space: normal;
  width: 100%;
}
.part-name {
  font-weight: 600;
}
.part-number {
  color: var(--grey-dark, #4a4a4a);
}
.child-pricing-loading {
  align-items: center;
  display: flex;
  gap: 0.25rem;
  margin-top: 0.5rem;
}
</style>
